<template>
  <div class="text-xs">
    <LoadingIndicator v-if="loading" message="Loading blocks..." />

    <div v-else-if="load_error" class="text-red-600">
      {{ load_error }}
      <button type="button" class="underline ml-1" @click="load">Retry</button>
    </div>

    <template v-else>
      <!-- Context strip: what you are editing and how many branches it has. -->
      <div class="flex items-baseline gap-2 mb-3 text-gray-500">
        <span class="font-mono text-gray-700">{{ view }}</span>
        <span>·</span>
        <span>{{ block_count }} blocks, one flat UNION ALL BY NAME</span>
        <span>·</span>
        <span>editing a block redeploys the whole view</span>
      </div>

      <div class="flex gap-4 items-start">
        <!-- ── Block list ──────────────────────────────────────────── -->
        <div class="w-[31.5rem] flex-shrink-0 border border-gray-200 rounded overflow-auto max-h-[640px]">
          <div v-for="g in groups" :key="g.key">
            <div
              class="sticky top-0 px-3 py-1.5 bg-gray-100 border-b border-gray-200
                     font-medium uppercase tracking-wide text-gray-500"
            >
              {{ g.label }}
              <span class="text-gray-400">({{ g.blocks.length }})</span>
            </div>
            <button
              v-for="b in g.blocks"
              :key="b.ordinal"
              type="button"
              class="w-full text-left px-3 py-2 border-b border-gray-100 hover:bg-gray-50"
              :class="isSelected(b) ? 'bg-gray-50 border-l-2 border-l-primary-500' : 'border-l-2 border-l-transparent'"
              @click="select(b)"
            >
              <div class="flex justify-between items-baseline gap-2">
                <span class="font-medium text-gray-800 truncate">{{ b.source || '(unnamed)' }}</span>
                <span
                  class="flex-shrink-0 px-1 rounded font-mono"
                  :class="relClass(b.reliability)"
                  :title="`SOURCE_RELIABILITY ${b.reliability} — lower wins`"
                >{{ b.reliability }}</span>
              </div>
              <div class="text-gray-500 truncate" :title="b.from">{{ b.from }}</div>
            </button>
          </div>
        </div>

        <!-- ── Editor ──────────────────────────────────────────────── -->
        <div class="flex-1 min-w-0">
          <div v-if="!selected" class="border border-gray-200 rounded p-6 text-gray-500">
            Select a block to edit.
          </div>

          <template v-else>
            <div class="flex items-center gap-3 mb-2">
              <span class="font-medium text-gray-800">{{ selected.source }}</span>
              <span class="text-gray-500">
                block {{ selected.ordinal }} ·
                {{ selected.group === 'cross_tenant' ? 'cross-tenant' : `tenant ${selected.tenant}` }} ·
                {{ selected.lines }} lines
              </span>
              <span v-if="dirty" class="text-primary-700 font-medium">unsaved</span>

              <div class="flex-1"></div>

              <button type="button" class="btn-secondary-sm" @click="editing = !editing">
                {{ editing ? 'Done' : 'Edit' }}
              </button>
              <button
                v-if="dirty"
                type="button"
                class="btn-secondary-sm"
                :disabled="busy"
                @click="revert"
              >
                Revert
              </button>
              <button type="button" class="btn-secondary-sm" :disabled="busy" @click="format">
                {{ formatting ? 'Formatting…' : 'Format' }}
              </button>
              <button type="button" class="btn-secondary-sm" :disabled="busy" @click="test">
                {{ testing ? 'Testing…' : 'Test Syntax' }}
              </button>
              <button type="button" class="btn-primary-sm" :disabled="busy || !dirty" @click="save">
                {{ saving ? 'Saving…' : 'Save' }}
              </button>
            </div>

            <!-- READ VIEW: one column per line, badged. Rendered from the
                 backend's per-line annotation, which is exact (1,248/1,248
                 column lines identified). Toggle to Edit for a raw textarea. -->
            <div
              v-if="!editing"
              class="h-[560px] overflow-auto border border-gray-200 rounded bg-white font-mono text-xs leading-relaxed"
            >
              <div
                v-for="l in annotated"
                :key="l.n"
                class="flex items-start gap-2 px-3 hover:bg-gray-50 cursor-text"
                @click="editAt(l.n)"
              >
                <span class="w-8 flex-shrink-0 text-right text-gray-300 select-none">{{ l.n }}</span>

                <!-- Badge gutter. Width is set by the LONGEST contract column
                     (OVERRIDE_TARGET_SOURCE_KEY, 26 chars) so a badge can never
                     bleed into the code; truncate is a backstop. -->
                <span class="w-52 flex-shrink-0 text-right overflow-hidden whitespace-nowrap">
                  <span
                    v-if="l.role === 'column'"
                    class="px-1 rounded"
                    :class="kindClass(l.kind)"
                    :title="`${l.column} — ${l.kind}`"
                  >{{ l.column }}</span>
                  <span
                    v-else-if="l.role === 'element'"
                    class="px-1 rounded"
                    :class="domainClass(l.domain)"
                    :title="elementTitle(l)"
                  >{{ l.type }}{{ l.verified === false ? '·rel' : '' }}{{ l.is_primary === false ? '·2nd' : '' }}</span>
                </span>

                <code
                  class="whitespace-pre flex-1 min-w-0 pl-3 border-l border-gray-100"
                  :class="lineClass(l)"
                >{{ l.text }}</code>
              </div>
            </div>

            <textarea
              v-else
              ref="editor"
              v-model="body"
              spellcheck="false"
              wrap="off"
              class="w-full h-[560px] font-mono text-xs leading-relaxed text-gray-800
                     border border-gray-200 rounded p-3 overflow-auto
                     focus:outline-none focus:border-gray-400"
            ></textarea>

            <!-- ── Result ─────────────────────────────────────────── -->
            <div
              v-if="result"
              class="mt-2 border rounded px-3 py-2"
              :class="result.ok
                ? 'border-green-dark bg-secondary-100 text-gray-800'
                : 'border-red-dark bg-primary-50 text-gray-800'"
            >
              <div class="flex items-start gap-2">
                <font-awesome-icon
                  :icon="['fas', result.ok ? 'circle-check' : 'circle-exclamation']"
                  :class="result.ok ? 'text-green-dark' : 'text-red-dark'"
                  class="mt-0.5 flex-shrink-0"
                />
                <div class="min-w-0">
                  <div class="font-medium">{{ headline }}</div>
                  <div v-if="detail" class="mt-1 font-mono whitespace-pre-wrap break-words text-gray-700">
                    {{ detail }}
                  </div>
                  <div v-if="result.grants" class="mt-1 text-gray-600">
                    Re-granted {{ result.grants.granted.join(', ') }}
                    <span v-if="grant_failures" class="text-red-dark">
                      · FAILED: {{ grant_failures }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { getBlocks, testSyntax, saveBlock, formatBlock } from '@/services/api/mdr/input';

export default {
  name: 'MDRInput',
  components: { LoadingIndicator },
  data() {
    return {
      loading: true,
      load_error: null,
      view: '',
      block_count: 0,
      cross_tenant: [],
      tenant_specific: {},
      selected: null,
      body: '',
      testing: false,
      formatting: false,
      editing: false,
      saving: false,
      result: null,
    };
  },
  computed: {
    /** Cross-tenant first (CMS/CCLF feeds every customer shares), then one
     *  section per tenant. The backend derives the grouping from each block's
     *  own SQL — scanning the view for DISTINCT TENANT_ID takes ~46s. */
    groups() {
      const out = [];
      if (this.cross_tenant.length) {
        out.push({ key: 'cross', label: 'Cross-tenant', blocks: this.cross_tenant });
      }
      Object.keys(this.tenant_specific).forEach((t) => {
        out.push({ key: `t${t}`, label: `Tenant ${t}`, blocks: this.tenant_specific[t] });
      });
      return out;
    },
    all_blocks() {
      return this.groups.flatMap((g) => g.blocks);
    },
    /** Backend annotation for the saved text; while dirty, fall back to a
     *  local pass so the read view still lines up with what you typed. */
    annotated() {
      if (!this.selected) return [];
      if (!this.dirty && this.selected.lines_annotated) return this.selected.lines_annotated;
      return this.body.split('\n').map((text, i) => ({ n: i + 1, text, role: 'sql' }));
    },
    busy() {
      return this.testing || this.saving || this.formatting;
    },
    dirty() {
      return !!this.selected && this.body !== this.selected.body;
    },
    headline() {
      const r = this.result;
      if (!r) return '';
      if (r.ok) {
        return r.stage === 'deployed'
          ? `Saved. View redeployed with ${r.block_count} blocks.`
          : `Syntax OK — all ${r.column_count} contract columns present.`;
      }
      if (r.stage === 'commented_out_sql') return 'Blocked: a comment would comment out live SQL.';
      if (r.stage === 'deploy') return 'Deploy failed — view unchanged.';
      if (r.stage === 'lookup') return 'Block not found in the live view.';
      const c = r.check || r;
      return c.error ? 'Syntax error.' : 'Column contract mismatch.';
    },
    detail() {
      const r = this.result;
      if (!r || r.ok) return '';
      const c = r.check || r;
      if (r.error) return r.error;
      if (c.error) return c.error;
      const out = [];
      const sw = r.lines || c.commented_out_sql;
      if (sw?.length) out.push(...sw.map((x) => `line ${x.line}: ${x.text}`));
      if (c.missing_columns?.length) out.push(`missing: ${c.missing_columns.join(', ')}`);
      if (c.extra_columns?.length) out.push(`unexpected: ${c.extra_columns.join(', ')}`);
      return out.join('\n');
    },
    grant_failures() {
      const f = this.result?.grants?.failed;
      return f && Object.keys(f).length ? Object.keys(f).join(', ') : '';
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    /** Lower rank = higher authority, so the palette darkens as rank drops. */
    /** NULL/literal recede; real expressions and JSON builders stand out. */
    kindClass(kind) {
      return {
        null: 'bg-gray-100 text-gray-400',
        literal: 'bg-gray-100 text-gray-600',
        column: 'bg-neutral-100 text-neutral-900',
        expr: 'bg-primary-100 text-primary-800',
        case: 'bg-primary-100 text-primary-800',
        json_array: 'bg-secondary-200 text-secondary-1000',
        json_object: 'bg-secondary-200 text-secondary-1000',
      }[kind] || 'bg-gray-100 text-gray-600';
    },
    domainClass(domain) {
      return {
        identifier: 'bg-neutral-300 text-neutral-1000',
        assignment: 'bg-secondary-200 text-secondary-1000',
        contact: 'bg-gray-200 text-gray-700',
        contract: 'bg-primary-100 text-primary-800',
        attribute: 'bg-gray-200 text-gray-700',
      }[domain] || 'bg-gray-100 text-gray-600';
    },
    lineClass(l) {
      if (l.role === 'comment') return 'text-gray-500';
      if (l.role === 'keyword') return 'text-neutral-900 font-semibold';
      if (l.role === 'column' && l.kind === 'null') return 'text-gray-400';
      return 'text-gray-800';
    },
    elementTitle(l) {
      const p = [l.domain, l.type];
      if (l.verified !== undefined) p.push(l.verified ? 'verified' : 'relayed (verified=FALSE)');
      if (l.is_primary !== undefined) p.push(l.is_primary ? 'is_primary' : 'secondary');
      return p.join(' · ');
    },
    relClass(rel) {
      if (rel === null || rel === undefined) return 'bg-gray-100 text-gray-500';
      if (rel <= 1) return 'bg-neutral-900 text-white';
      if (rel === 2) return 'bg-neutral-700 text-white';
      if (rel <= 4) return 'bg-neutral-300 text-neutral-900';
      return 'bg-gray-200 text-gray-600';
    },
    isSelected(b) {
      return this.selected && this.selected.ordinal === b.ordinal;
    },
    async load() {
      this.loading = true;
      this.load_error = null;
      try {
        const data = await getBlocks();
        this.view = data.view;
        this.block_count = data.block_count;
        this.cross_tenant = data.cross_tenant || [];
        this.tenant_specific = data.tenant_specific || {};
      } catch (e) {
        this.load_error = e?.response?.data?.detail || e.message || "Couldn't load blocks.";
      } finally {
        this.loading = false;
      }
    },
    select(b) {
      // Never silently discard an edit when clicking to another block.
      if (this.dirty && !window.confirm('Discard unsaved changes to this block?')) return;
      this.selected = b;
      this.body = b.body;
      this.result = null;
    },
    /** Click a line in the read view to edit it -- caret lands on that line. */
    editAt(n) {
      this.editing = true;
      this.$nextTick(() => {
        const ta = this.$refs.editor;
        if (!ta) return;
        const lines = this.body.split('\n');
        const pos = lines.slice(0, n - 1).join('\n').length + (n > 1 ? 1 : 0);
        ta.focus();
        ta.setSelectionRange(pos, pos);
        // Put the target line near the top rather than wherever it happened to be.
        ta.scrollTop = Math.max(0, (n - 3) * 18);
      });
    },
    revert() {
      this.body = this.selected.body;
      this.result = null;
    },
    async format() {
      this.formatting = true;
      try {
        const r = await formatBlock(this.body);
        if (r.ok) this.body = r.body;
      } catch (e) {
        this.result = { ok: false, error: e?.response?.data?.error || e.message };
      } finally {
        this.formatting = false;
      }
    },
    async test() {
      this.testing = true;
      this.result = null;
      try {
        this.result = await testSyntax(this.body);
      } catch (e) {
        this.result = { ok: false, error: e?.response?.data?.error || e.message };
      } finally {
        this.testing = false;
      }
    },
    async save() {
      // The view feeds the whole EMPI engine and a save replaces it live, so
      // make the blast radius explicit rather than one accidental click.
      const ok = window.confirm(
        `Replace ${this.selected.source} in the live PATIENT_ALL view?\n\n`
        + 'This redeploys all 32 blocks and re-grants. The next EMPI run '
        + '(Sat 08:00 UTC) will use it.',
      );
      if (!ok) return;
      this.saving = true;
      this.result = null;
      try {
        const r = await saveBlock(this.selected.ordinal, this.body);
        this.result = r;
        // Re-read from the live view so the list shows what is actually
        // deployed, not what we think we sent.
        if (r.ok) {
          const ord = this.selected.ordinal;
          await this.load();
          const again = this.all_blocks.find((b) => b.ordinal === ord);
          if (again) {
            this.selected = again;
            this.body = again.body;
          }
        }
      } catch (e) {
        this.result = e?.response?.data || { ok: false, error: e.message };
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
